import { isAppRelativeUrl, resolveSameOriginUrl } from '@awork/_shared/functions/app-relative-url'
import { isSafeDocumentLink, normalizeDocumentLinkInput } from '@awork/features/document/editor-schema/document-links'
import { WorkspaceQuery } from '@awork/features/workspace/state/workspace.query'
import type { Observable, Subscription } from 'rxjs'
import { firstValueFrom, fromEvent, merge, mapTo, from, Subject, take, takeUntil } from 'rxjs'
import type { ElementRef, OnDestroy } from '@angular/core'
import { Injectable, NgZone, inject, signal } from '@angular/core'
import { InAppBrowser } from '@awesome-cordova-plugins/in-app-browser/ngx'
import { AppUpdate } from '@capawesome/capacitor-app-update'
import type { KeyboardInfo } from '@capacitor/keyboard'
import { Keyboard } from '@capacitor/keyboard'
import getLanguage from '@awork/_shared/functions/get-language'
import { apiHostname } from '@awork/environments/environment'
import { Device } from '@capacitor/device'
import { Capacitor } from '@capacitor/core'

@Injectable({
  providedIn: 'root'
})
export class BrowserService implements OnDestroy {
  private iab = inject(InAppBrowser)
  private workspaceQuery = inject(WorkspaceQuery)
  private ngZone = inject(NgZone)

  public keyboardWillShow$ = new Subject<KeyboardInfo>()
  public keyboardWillHide$ = new Subject<void>()
  public keyboardDidShow$ = new Subject<KeyboardInfo>()
  public keyboardDidHide$ = new Subject<void>()

  private subscriptions: Subscription[] = []

  public keyboardHeight = 333 // iphone XS height
  public keyboardShown = false

  online$: Observable<boolean>

  private scrollingElement: ElementRef
  private readonly currentViewportHeight = signal<number | null>(null)
  readonly viewportHeight = this.currentViewportHeight.asReadonly()
  private pageScrollLocks = 0
  private releasePageScroll?: () => void

  constructor() {
    this.init()
  }

  init(): void {
    this.initKeyboardEvents()

    this.online$ = merge(fromEvent(window, 'online').pipe(mapTo(true)), fromEvent(window, 'offline').pipe(mapTo(false)))
  }

  ngOnDestroy(): void {
    this.releasePageScroll?.()
    this.releasePageScroll = undefined
    this.subscriptions.forEach(sub => {
      if (sub && !sub.closed) {
        sub.unsubscribe()
      }
    })
  }

  /**
   * Locks document scrolling while a full-screen flow owns its internal scroll area.
   * @returns {() => void} An idempotent release callback. Scrolling resumes after the final owner leaves.
   */
  lockPageScroll(): () => void {
    if (this.pageScrollLocks === 0) {
      this.releasePageScroll = this.startPageScrollLock()
    }

    this.pageScrollLocks += 1

    let released = false

    return () => {
      if (released) {
        return
      }

      released = true
      this.pageScrollLocks -= 1

      if (this.pageScrollLocks === 0) {
        this.releasePageScroll?.()
        this.releasePageScroll = undefined
      }
    }
  }

  /**
   * Owns the document lock, native scroll toggle, and viewport listener until release.
   * @returns {() => void} Cleanup for the current page lock.
   */
  private startPageScrollLock(): () => void {
    const viewport = window.visualViewport
    const roots = [document.documentElement, document.body]
    const isIos = Capacitor.getPlatform() === 'ios'
    const syncHeight = () => this.currentViewportHeight.set(viewport?.height ?? null)

    roots.forEach(root => root.classList.add('aw-page-scroll-locked'))
    syncHeight()
    viewport?.addEventListener('resize', syncHeight)

    if (isIos) {
      void Keyboard.setScroll({ isDisabled: true })
    }

    return () => {
      roots.forEach(root => root.classList.remove('aw-page-scroll-locked'))
      viewport?.removeEventListener('resize', syncHeight)
      this.currentViewportHeight.set(null)

      if (isIos) {
        void Keyboard.setScroll({ isDisabled: false })
      }
    }
  }

  /**
   * Initializes the keyboard events
   */
  private initKeyboardEvents(): void {
    if (Capacitor.getPlatform() === 'web') {
      return
    }

    // hide the iOS top keyboard action to navigate through the forms
    from(Device.getInfo()).subscribe(deviceInfo => {
      if (deviceInfo.platform === 'ios') {
        Keyboard.setAccessoryBarVisible({ isVisible: false }).then(() => {})
      }
    })

    Keyboard.addListener('keyboardWillShow', (keyboardInfo: KeyboardInfo) => {
      this.ngZone.run(() => {
        this.keyboardWillShow$.next(keyboardInfo)
      })
    })

    Keyboard.addListener('keyboardWillHide', () => {
      this.ngZone.run(() => {
        this.keyboardWillHide$.next()
      })
    })

    Keyboard.addListener('keyboardDidShow', (keyboardInfo: KeyboardInfo) => {
      this.ngZone.run(() => {
        this.keyboardDidShow$.next(keyboardInfo)
      })
    })

    Keyboard.addListener('keyboardDidHide', () => {
      this.ngZone.run(() => {
        this.keyboardDidHide$.next()
      })
    })

    // subscribe to the keyboard show and hide events, to save the state and height
    this.subscriptions.push(
      this.keyboardWillShow$.subscribe(event => {
        this.keyboardHeight = event.keyboardHeight
        this.keyboardShown = true
      })
    )
    this.subscriptions.push(
      this.keyboardDidHide$.subscribe(() => {
        this.keyboardShown = false
        this.correctBodyScrollPosition()
      })
    )
  }

  /**
   * Shows the in app browser with the given url
   * @param {string} url
   */
  showBrowser(url: string): void {
    if (url) {
      const options =
        'location=no,hideurlbar=yes,toolbarcolor=#F4FAFF,footercolor=#F4FAFF,hidespinner=yes,closebuttoncolor=#FFFFFF,closebuttoncaption=Close'

      const browser = this.iab.create(url, '_blank', options)

      browser.show()
      // Listen to exit event to close the browser
      browser.on('exit').subscribe(() => browser.close())
    }

    // TODO: This would be a better expierence for iOS. Could think about it
    // https://github.com/EddyVerbruggen/cordova-plugin-safariviewcontroller
  }

  /** Opens a URL with the native system handler so links can hand off to other apps.
   * @param {string} url URL to open outside the embedded browser.
   */
  openSystemBrowser(url: string): void {
    if (url) {
      this.iab.create(url, '_system')
    }
  }

  /**
   * Opens an isolated authorization window and waits for the user to close it.
   * @param {string} url Authorization URL returned by the backend.
   * @param {AbortSignal} signal Cancels ownership when the originating agent view closes.
   */
  async authorize(url: string, signal: AbortSignal): Promise<void> {
    if (signal.aborted) {
      return
    }

    const browser = this.iab.create(url, '_blank', 'location=yes,hideurlbar=no')
    const closed = firstValueFrom(browser.on('exit').pipe(take(1), takeUntil(fromEvent(signal, 'abort'))))

    browser.show()

    try {
      await closed
    } finally {
      browser.close()
    }
  }

  /**
   * Opens the app in the store
   */
  showAppInStore(): void {
    AppUpdate.openAppStore()
  }

  /**
   * Opens the browser, with the login screens for awork web and shows the subscription modal
   */
  showBookingInBrowser(): void {
    if (window.confirm(q.translations.BrowserService.bookingConfirm)) {
      // TODO: this needs to be optimized/refactored !!!
      window.open(`${this.appOrigin('bwork.io')}?modal=subscribe`, '_system')
    }
  }

  /**
   * Opens the browser, with the login screens for awork web
   */
  showAppInBrowser(): void {
    this.showBrowser(this.appOrigin())
  }

  /**
   * Opens a route from the awork web app in the in-app browser.
   * @param {readonly (string | number)[]} route - Web-app route segments.
   */
  showAppRouteInBrowser(route: readonly (string | number)[]): void {
    const path = route.map(segment => encodeURIComponent(String(segment).replace(/^\/+/, ''))).join('/')

    this.showAppUrlInBrowser(`/${path}`)
  }

  /**
   * Opens a current-workspace URL, preserving its query and fragment and refusing other origins.
   * @param {string} url Relative app URL or absolute URL on the current awork host.
   */
  showAppUrlInBrowser(url: string): void {
    const resolved = resolveSameOriginUrl(url, this.appOrigin())

    if (resolved) {
      this.showBrowser(resolved.toString())
    }
  }

  /**
   * Opens a document link, resolving app-relative destinations through the workspace origin.
   * @param {string} href Persisted document link.
   */
  showDocumentLink(href: string): void {
    if (!isSafeDocumentLink(href)) {
      return
    }

    if (isAppRelativeUrl(href, this.appOrigin())) {
      this.showAppUrlInBrowser(href)
    } else {
      this.showBrowser(normalizeDocumentLinkInput(href) ?? href)
    }
  }

  /**
   * Opens the browser, with the login screens for awork web and shows the profile settings
   */
  showSettingsInBrowser(): void {
    window.open(`${this.appOrigin()}//my/profile/notifications`, '_system')
  }

  /**
   * Gets the current workspace origin for web navigation.
   * @param {string} [hostname=apiHostname] App host, or the legacy booking host.
   * @returns {string} Workspace web origin.
   */
  private appOrigin(hostname = apiHostname): string {
    const subdomain = this.workspaceQuery.getCurrentWorkspace().subdomains[0].name

    return `https://${subdomain}.${hostname}`
  }

  /**
   * Opens the browser, with the pricing info
   */
  showPricingInBrowser(): void {
    let pricingUrl = 'https://www.awork.com/pricing/'

    if (getLanguage() === 'de') {
      pricingUrl = 'https://www.awork.com/de/pricing/'
    }

    this.showBrowser(pricingUrl)
  }

  /**
   * Shows the terms & conditins in the browser
   */
  showTermsInBrowser(): void {
    let legalUrl = 'https://www.awork.com/legal-and-privacy/'

    if (getLanguage() === 'de') {
      legalUrl = 'https://www.awork.com/de/legal-and-privacy/'
    }

    this.showBrowser(legalUrl)
  }

  /**
   * Shows the docs in the browser
   */
  showHelpInBrowser(): void {
    let helpUrl = 'https://support.awork.com/en/articles/awork-mobile-app-ios-and-android-CGahmxpwnIGJ'

    if (getLanguage() === 'de') {
      helpUrl = 'https://support.awork.com/de/articles/awork-mobile-app-ios-and-android-CGahmxpwnIGJ'
    }

    this.showBrowser(helpUrl)
  }

  /**
   * toggles the class on the body to show/hide the caret/cursor
   * TODO: Somehoy doesn't work ... :/
   * @param {boolean} value
   */
  applyMotionClass(value: boolean): void {
    const el = document.body

    if (el) {
      if (el.classList.contains('aw-move') && !value) {
        document.body.classList.remove('aw-move')
      } else if (!el.classList.contains('aw-move') && value) {
        document.body.classList.add('aw-move')
      }
    }
  }

  setScrollingElement(element: ElementRef): void {
    this.scrollingElement = element
  }

  getScrollingElement(): ElementRef {
    return this.scrollingElement
  }

  correctBodyScrollPosition(): void {
    document.documentElement.scrollTop = 0
  }
}
